// Phase P1 — BlackRock Band (Option C from the planning doc).
//
// Single horizontal band split into 5 sections, one per universal classification
// band (Critical Condition / Unstable / Growth Ready / High Performing / Elite
// Operations). The section the score falls in is rendered in gold; the others
// in graphite. A thin offwhite notch with a gold cap dot crosses the band at
// the exact score position.
//
// Design contract (CEO-locked):
//   - Band thresholds: 0-25, 26-50, 51-75, 76-90, 91-100
//   - Band labels: the 5 CEO-locked strings above. NEVER varied per-metric.
//   - Per-metric label override (e.g. Automation "Autonomous" instead of
//     "Elite Operations") is rendered ABOVE the band, NOT inside it.
//   - No gradients. Solid fills only. Monochrome + gold accent.
//   - Primary variant: ~120px tall, score-number floats above the notch.
//   - Supporting variant: ~64px tall, compact, score-number aligned right.

import PropTypes from "prop-types";

const BANDS = [
  { id: "critical",  lo:  0, hi:  25, label: "Critical Condition" },
  { id: "unstable",  lo: 26, hi:  50, label: "Unstable" },
  { id: "growth",    lo: 51, hi:  75, label: "Growth Ready" },
  { id: "high_perf", lo: 76, hi:  90, label: "High Performing" },
  { id: "elite",     lo: 91, hi: 100, label: "Elite Operations" },
];

// Width of each band on a 0-100 scale → cumulative breakpoints
// [0, 25, 50, 75, 90, 100] map to flex weights [25, 25, 25, 15, 10].
const BAND_FLEX_WEIGHTS = [25, 25, 25, 15, 10];

function notchPercent(score) {
  const n = Math.max(0, Math.min(100, Number(score) || 0));
  return n; // notch sits at score% of the bar width
}

function bandIdForScore(score) {
  const n = Math.max(0, Math.min(100, Number(score) || 0));
  return BANDS.find((b) => n >= b.lo && n <= b.hi)?.id ?? "elite";
}

export default function BlackRockBand({
  score,
  label,
  bandId,
  explanation,
  metricLabel,
  size,
  testId,
}) {
  const active = bandId || bandIdForScore(score);
  const isPrimary = size === "primary";
  const totalHeight = isPrimary ? 120 : 64;
  const numberSize = isPrimary ? 56 : 28;
  const labelSize = isPrimary ? 13 : 10;

  return (
    <div
      data-testid={testId || `score-band-${active}`}
      data-band-id={active}
      data-score={score}
      style={{
        position: "relative",
        width: "100%",
        marginTop: isPrimary ? 36 : 18,
        paddingTop: isPrimary ? 32 : 18,
      }}
    >
      {/* Metric label row (above band) */}
      {metricLabel && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: 10,
          }}
        >
          <span
            className="sf-mono"
            style={{
              fontSize: 11,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "var(--sf-muted)",
            }}
          >
            {metricLabel}
          </span>
          <span
            className="sf-mono"
            data-testid={`${testId || "score-band"}-label-text`}
            style={{
              fontSize: labelSize,
              letterSpacing: "0.10em",
              textTransform: "uppercase",
              color: "var(--sf-gold)",
            }}
          >
            {label}
          </span>
        </div>
      )}

      {/* Floating score number (primary only) */}
      {isPrimary && (
        <div
          data-testid={`${testId || "score-band"}-number`}
          style={{
            fontFamily: "var(--sf-display, 'Bricolage Grotesque', serif)",
            fontWeight: 700,
            fontSize: numberSize,
            lineHeight: 1,
            color: "var(--sf-offwhite)",
            marginBottom: 12,
            letterSpacing: "-0.02em",
          }}
        >
          {score}
          <span
            className="sf-mono"
            style={{
              fontSize: numberSize * 0.32,
              color: "var(--sf-muted)",
              marginLeft: 8,
            }}
          >
            /100
          </span>
        </div>
      )}

      {/* The band itself */}
      <div
        style={{
          position: "relative",
          display: "flex",
          width: "100%",
          height: totalHeight,
          border: "1px solid var(--sf-border, #2B3036)",
          borderRadius: 4,
          overflow: "hidden",
          backgroundColor: "var(--sf-charcoal, #16191D)",
        }}
      >
        {BANDS.map((b, idx) => {
          const isActive = b.id === active;
          return (
            <div
              key={b.id}
              data-testid={`${testId || "score-band"}-section-${b.id}`}
              data-active={isActive ? "true" : "false"}
              style={{
                flex: BAND_FLEX_WEIGHTS[idx],
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 6,
                paddingInline: 6,
                backgroundColor: isActive
                  ? "var(--sf-gold, #C9A24A)"
                  : "var(--sf-graphite, #22262B)",
                borderRight:
                  idx < BANDS.length - 1
                    ? "1px solid var(--sf-border, #2B3036)"
                    : "none",
                transition: "background-color 200ms ease",
              }}
            >
              <span
                className="sf-mono"
                style={{
                  fontSize: 10,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: isActive
                    ? "var(--sf-black, #0B0F12)"
                    : "var(--sf-muted, #8A8F96)",
                  textAlign: "center",
                  lineHeight: 1.15,
                }}
              >
                {b.label}
              </span>
              <span
                className="sf-mono"
                style={{
                  fontSize: 9,
                  color: isActive
                    ? "var(--sf-black, #0B0F12)"
                    : "var(--sf-muted, #8A8F96)",
                  opacity: isActive ? 0.65 : 0.7,
                }}
              >
                {b.lo}–{b.hi}
              </span>
            </div>
          );
        })}

        {/* Notch — vertical offwhite line at score% with gold cap dot */}
        <div
          aria-hidden
          data-testid={`${testId || "score-band"}-notch`}
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: `${notchPercent(score)}%`,
            width: 2,
            backgroundColor: "var(--sf-offwhite, #EDEDED)",
            transform: "translateX(-1px)",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: -5,
              left: "50%",
              transform: "translateX(-50%)",
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "var(--sf-gold, #C9A24A)",
              border: "1px solid var(--sf-offwhite, #EDEDED)",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: -5,
              left: "50%",
              transform: "translateX(-50%)",
              width: 10,
              height: 10,
              borderRadius: "50%",
              backgroundColor: "var(--sf-gold, #C9A24A)",
              border: "1px solid var(--sf-offwhite, #EDEDED)",
            }}
          />
        </div>
      </div>

      {/* Score readout for supporting variant — sits beneath the band, right-aligned */}
      {!isPrimary && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginTop: 8,
            gap: 12,
          }}
        >
          {explanation ? (
            <span
              style={{
                fontSize: 13,
                color: "var(--sf-offwhite)",
                lineHeight: 1.45,
                opacity: 0.85,
                maxWidth: "78%",
              }}
              data-testid={`${testId || "score-band"}-explanation`}
            >
              {explanation}
            </span>
          ) : (
            <span />
          )}
          <span
            className="sf-mono"
            style={{
              fontSize: 22,
              color: "var(--sf-offwhite)",
              fontWeight: 600,
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap",
            }}
            data-testid={`${testId || "score-band"}-number`}
          >
            {score}
            <span style={{ color: "var(--sf-muted)", fontSize: 14, marginLeft: 4 }}>
              /100
            </span>
          </span>
        </div>
      )}

      {/* Explanation paragraph for primary variant */}
      {isPrimary && explanation && (
        <p
          data-testid={`${testId || "score-band"}-explanation`}
          style={{
            marginTop: 18,
            marginBottom: 0,
            fontSize: 15,
            color: "var(--sf-offwhite)",
            lineHeight: 1.55,
            maxWidth: "44em",
          }}
        >
          {explanation}
        </p>
      )}
    </div>
  );
}

BlackRockBand.propTypes = {
  score: PropTypes.number.isRequired,
  label: PropTypes.string,           // per-metric label (e.g. "Autonomous")
  bandId: PropTypes.string,          // canonical band id; auto-derived if absent
  explanation: PropTypes.string,
  metricLabel: PropTypes.string,     // section header (e.g. "Automation Readiness")
  size: PropTypes.oneOf(["primary", "supporting"]),
  testId: PropTypes.string,
};

BlackRockBand.defaultProps = {
  label: "",
  bandId: "",
  explanation: "",
  metricLabel: "",
  size: "supporting",
  testId: "",
};
